/**
 * Shared UI translations (header, footer, cookie banner, hero).
 * Page-specific legal copy lives inside each page component.
 */
export const NAV = {
    en: {
        home: "Home",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        childSafety: "Child Safety",
        cookies: "Cookie Policy",
        contact: "Contact & Support",
        legal: "Legal Notice",
    },
    de: {
        home: "Startseite",
        privacy: "Datenschutz",
        terms: "Nutzungsbedingungen",
        childSafety: "Kinderschutz",
        cookies: "Cookie-Richtlinie",
        contact: "Kontakt & Support",
        legal: "Impressum",
    },
};

export const PATHS = {
    home: "/",
    privacy: "/privacy-policy",
    terms: "/terms-of-service",
    childSafety: "/child-safety",
    cookies: "/cookie-policy",
    contact: "/contact",
    legal: "/legal-notice",
};

export const FOOTER = {
    en: {
        tagline: "Discover Dinosaurs Through Math!",
        about:
            "Dino Detective is a family-friendly educational game that helps children improve math skills while discovering amazing dinosaurs.",
        sections: { legal: "Legal", support: "Support" },
        rights: "All rights reserved.",
        ownerLine: "Owned and operated by Jorge Miguel Oliveira Gomes — Mörfelden-Walldorf, Germany.",
        emailLabel: "Support email",
    },
    de: {
        tagline: "Entdecke Dinosaurier durch Mathematik!",
        about:
            "Dino Detective ist ein familienfreundliches Lernspiel, das Kindern hilft, Mathekenntnisse zu verbessern, während sie faszinierende Dinosaurier entdecken.",
        sections: { legal: "Rechtliches", support: "Support" },
        rights: "Alle Rechte vorbehalten.",
        ownerLine: "Betrieben von Jorge Miguel Oliveira Gomes — Mörfelden-Walldorf, Deutschland.",
        emailLabel: "Support-E-Mail",
    },
};

export const COOKIE = {
    en: {
        title: "We use only essential cookies.",
        body:
            "Dino Detective’s website uses essential and security cookies to keep the site working safely. We do not use advertising or analytics cookies, Google Analytics or Meta Pixel.",
        accept: "Accept All",
        reject: "Reject Non-Essential",
        manage: "Manage Preferences",
        save: "Save preferences",
        prefsTitle: "Cookie Preferences",
        prefsBody:
            "You can choose which cookies to allow below. Essential cookies are always on because the website cannot function without them.",
        essential: "Essential cookies",
        essentialDesc: "Required for the site to load, navigate and stay secure. Always on.",
        security: "Security cookies",
        securityDesc: "Help protect against abuse and keep the site safe. Always on.",
        nonEssential: "Non-essential cookies",
        nonEssentialDesc:
            "We do not currently use any non-essential cookies. This toggle is here for transparency.",
    },
    de: {
        title: "Wir verwenden nur essenzielle Cookies.",
        body:
            "Die Website von Dino Detective verwendet nur essenzielle und Sicherheits-Cookies, damit die Seite sicher funktioniert. Wir verwenden keine Werbe- oder Analyse-Cookies, kein Google Analytics und kein Meta Pixel.",
        accept: "Alle akzeptieren",
        reject: "Nicht essenzielle ablehnen",
        manage: "Einstellungen verwalten",
        save: "Einstellungen speichern",
        prefsTitle: "Cookie-Einstellungen",
        prefsBody:
            "Du kannst unten wählen, welche Cookies du zulassen möchtest. Essenzielle Cookies sind immer aktiv, da die Website ohne sie nicht funktioniert.",
        essential: "Essenzielle Cookies",
        essentialDesc: "Notwendig zum Laden, Navigieren und für die Sicherheit der Seite. Immer aktiv.",
        security: "Sicherheits-Cookies",
        securityDesc: "Schützen vor Missbrauch und halten die Seite sicher. Immer aktiv.",
        nonEssential: "Nicht-essenzielle Cookies",
        nonEssentialDesc:
            "Wir verwenden derzeit keine nicht-essenziellen Cookies. Dieser Schalter dient nur der Transparenz.",
    },
};

export const HOME = {
    en: {
        eyebrow: "Educational dinosaur game for families",
        title1: "Discover",
        title2: "Dinosaurs",
        title3: "Through Math!",
        subtitle:
            "Dino Detective helps children improve their math skills while exploring 100 amazing dinosaurs — solve puzzles, build collections, earn rewards and learn fascinating prehistoric facts.",
        googlePlay: "Google Play",
        getItOn: "Get it on",
        ctaTitle: "Ready to dig up some math?",
        ctaBody: "Dino Detective is now live on Google Play — download it, unlock the collection and start your prehistoric math adventure.",
        ctaButton: "Open in Google Play",
        featuresTitle: "Big roar. Bigger brain workout.",
        featuresSubtitle:
            "Every part of Dino Detective is designed to be safe, fun and genuinely educational — built for curious kids and trusting parents.",
        features: [
            { title: "100 Dinosaur Puzzles", desc: "A hundred lovingly designed puzzles, each unlocking a new prehistoric friend." },
            { title: "Math Challenges", desc: "Addition, subtraction, multiplication and logic — tuned to grow with your child." },
            { title: "Dinosaur Collection", desc: "Collect, organize and admire your roster of unlocked dinosaurs." },
            { title: "Collector Rewards", desc: "Earn badges, trophies and surprises as your collection grows." },
            { title: "Personalized Certificates", desc: "Celebrate milestones with custom certificates your kid can be proud of." },
            { title: "Child-Friendly", desc: "Designed for children: no scary content, no chat, no social features." },
            { title: "Offline Gameplay", desc: "Play anywhere — on the plane, in the car, or off the grid." },
            { title: "No Third-Party Ads", desc: "Zero ads, zero ad networks tracking your child. Promise." },
        ],
        premiumTitle: "Premium that respects your family.",
        premiumLead:
            "Unlock the full prehistoric world with a single one-time purchase. No subscriptions, no recurring charges, no surprises.",
        premiumBullets: [
            "One-time purchase",
            "No subscription",
            "Restore purchases supported",
            "Same price forever",
        ],
        premiumNote: "Billing is handled securely by Google Play. We never see or store your card details.",
    },
    de: {
        eyebrow: "Lern-Dinospiel für Familien",
        title1: "Entdecke",
        title2: "Dinosaurier",
        title3: "durch Mathe!",
        subtitle:
            "Dino Detective hilft Kindern, ihre Mathekenntnisse zu verbessern, während sie 100 faszinierende Dinosaurier entdecken — Rätsel lösen, Sammlungen aufbauen, Belohnungen verdienen und prähistorische Fakten lernen.",
        googlePlay: "Google Play",
        getItOn: "Erhältlich bei",
        ctaTitle: "Bereit, Mathe auszugraben?",
        ctaBody: "Dino Detective ist jetzt bei Google Play verfügbar — jetzt herunterladen, Sammlung freischalten und dein Urzeit-Abenteuer beginnen.",
        ctaButton: "In Google Play öffnen",
        featuresTitle: "Lautes Brüllen. Großes Gehirntraining.",
        featuresSubtitle:
            "Jeder Teil von Dino Detective ist sicher, unterhaltsam und wirklich lehrreich gestaltet — für neugierige Kinder und vertrauensvolle Eltern.",
        features: [
            { title: "100 Dino-Rätsel", desc: "Hundert liebevoll gestaltete Rätsel, die jeweils einen neuen Urzeit-Freund freischalten." },
            { title: "Mathe-Aufgaben", desc: "Plus, Minus, Mal und Logik — abgestimmt auf den Lernfortschritt deines Kindes." },
            { title: "Dino-Sammlung", desc: "Sammle, ordne und bewundere alle freigeschalteten Dinosaurier." },
            { title: "Sammler-Belohnungen", desc: "Verdiene Abzeichen, Trophäen und Überraschungen, während deine Sammlung wächst." },
            { title: "Personalisierte Urkunden", desc: "Feiere Erfolge mit Urkunden, auf die dein Kind stolz sein kann." },
            { title: "Kinderfreundlich", desc: "Für Kinder entwickelt: keine gruseligen Inhalte, kein Chat, keine sozialen Funktionen." },
            { title: "Offline spielbar", desc: "Spiele überall — im Flugzeug, im Auto oder ganz ohne Internet." },
            { title: "Keine Drittanbieter-Werbung", desc: "Null Werbung, null Werbenetzwerke, die dein Kind verfolgen. Versprochen." },
        ],
        premiumTitle: "Premium, das deine Familie respektiert.",
        premiumLead:
            "Schalte die gesamte Urzeitwelt mit einem einzigen Einmalkauf frei. Kein Abo, keine wiederkehrenden Gebühren, keine Überraschungen.",
        premiumBullets: [
            "Einmaliger Kauf",
            "Kein Abonnement",
            "Käufe wiederherstellbar",
            "Gleicher Preis für immer",
        ],
        premiumNote: "Die Abwicklung erfolgt sicher über Google Play. Wir sehen oder speichern keine Kartendaten.",
    },
};

export const PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=app.dinodetective.mathadventure2";

export const SUPPORT_EMAIL = "support@dino-detective.app";
export const OWNER_NAME = "Jorge Miguel Oliveira Gomes";
export const OWNER_ADDRESS = "Treburer Straße 49, 64546 Mörfelden-Walldorf, Germany";
export const LAST_UPDATED = "December 2025";
